import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { PopoverInteractionKind } from '@blueprintjs/core';
import { withTheme } from 'styled-components';
import { Highcharts, Box, Text, Flex, Icon, Link, Tooltip } from 'core/components';
import { FiExternalLink } from 'react-icons/fi';
import { addCommas } from 'app/util/utils';
import SudoTaggedText, { SudoTagPosition } from 'app/components/SudoTaggedText';
import PlanCardVizHeader from 'app/views/licenses/TrafficPakPlans/PlanCardVizHeader';
import LoadingSkeleton from 'app/views/metrics/LoadingSkeleton';
import {
  buildReferenceLineData,
  getLookbackDurationSeconds,
  hasNonZeroData
} from 'app/util/deviceFlowMetricsQueryUtils';
import DataResolutionIndicator from './DataResolutionIndicator';

@inject('$colors', '$auth')
@withTheme
@observer
export default class UtilizationChart extends Component {
  // Time period options
  timePeriodOptions = [
    { value: '1_day', label: '24 Hours' },
    { value: '7_days', label: '7 Days' },
    { value: '30_days', label: '30 Days' }
  ];

  handleTimePeriodChange = (selectedValue) => {
    const { onTimePeriodChange } = this.props;
    if (onTimePeriodChange && selectedValue) {
      // Convert value back to option object for compatibility with parent
      const selectedOption = this.timePeriodOptions.find((option) => option.value === selectedValue);
      if (selectedOption) {
        onTimePeriodChange(selectedOption);
      }
    }
  };

  get chartConfig() {
    const { receivedFlowArray, storedFlowArray, pak_fps, selectedTimePeriod, $colors, theme } = this.props;

    const createSeriesData = (data) => {
      if (!data || !Array.isArray(data)) {
        return [];
      }
      return data.filter((point) => Array.isArray(point) && point.length >= 2).map((point) => [point[0], point[1]]);
    };

    const receivedData = createSeriesData(receivedFlowArray);
    const storedData = createSeriesData(storedFlowArray);
    const hasData = hasNonZeroData([{ data: receivedData }, { data: storedData }]);

    // Flat "Max FPS" reference series, matching the FlowPak card styling. When there is no flow
    // data, the timestamps are synthesized from the lookback so the line, axes, and legend render.
    const maxFpsSeriesData = pak_fps
      ? buildReferenceLineData({
          flowSeries: [storedData, receivedData],
          value: pak_fps,
          lookbackSeconds: getLookbackDurationSeconds(selectedTimePeriod)
        })
      : [];

    return {
      chart: {
        type: 'line',
        animation: false,
        backgroundColor: 'transparent'
      },
      title: {
        text: null
      },
      credits: {
        enabled: false
      },
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
          millisecond: '%H:%M:%S',
          second: '%H:%M:%S',
          minute: '%H:%M',
          hour: '%H:%M',
          day: '%m/%d',
          week: '%m/%d',
          month: '%m/%y',
          year: '%Y'
        },
        labels: {
          style: {
            color: theme.colors.chart.legendLabels,
            fontSize: '11px'
          }
        },
        lineColor: theme.colors.chart.legendLabels,
        lineWidth: 1,
        tickColor: theme.colors.chart.legendLabels,
        tickWidth: 1
      },
      yAxis: {
        title: {
          text: null
        },
        labels: {
          formatter() {
            if (this.value >= 1000) {
              return `${this.value / 1000}k`;
            }
            return this.value;
          },
          style: {
            color: theme.colors.chart.legendLabels,
            fontSize: '11px'
          }
        },
        gridLineWidth: 1,
        min: 0,
        max: hasData || !pak_fps ? undefined : pak_fps
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        itemStyle: {
          fontSize: '11px',
          color: theme.colors.chart.legendLabels
        },
        symbolHeight: 8,
        symbolWidth: 8,
        symbolRadius: 0
      },
      plotOptions: {
        line: {
          animation: false,
          lineWidth: 2,
          marker: {
            enabled: false,
            states: {
              hover: {
                enabled: true,
                radius: 4
              }
            }
          }
        },
        series: {
          turboThreshold: 0
        }
      },
      tooltip: {
        shared: true,
        crosshairs: true,
        valueSuffix: 'FPS',
        valueDecimals: 1,
        xDateFormat: '%m/%d %H:%M',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderRadius: 3,
        shadow: false
      },
      series: [
        {
          name: 'Received',
          data: receivedData,
          color: $colors?.qualitativeColors?.[0],
          lineWidth: 2,
          zIndex: 1
        },
        {
          name: 'Stored',
          data: storedData,
          color: $colors?.qualitativeColors?.[1],
          dashStyle: 'ShortDot',
          lineWidth: 2,
          zIndex: 1
        },
        ...(pak_fps
          ? [
              {
                name: 'Max FPS',
                data: maxFpsSeriesData,
                color: $colors?.qualitativeColors?.[2],
                lineWidth: 2,
                zIndex: 1
              }
            ]
          : [])
      ]
    };
  }

  render() {
    const { hideGoToPublicCloudsButton, $auth } = this.props;
    const {
      avgConfiguredSamplingRate,
      dataResolution,
      description,
      loading,
      error,
      deviceCount,
      receivedFlowValue,
      rawFlowValue,
      storedFlowValue,
      planMaxFps,
      selectedTimePeriod
    } = this.props;
    // Calculate percentages for display
    const receivedPercentage = receivedFlowValue ? ((receivedFlowValue / planMaxFps) * 100).toFixed(2) : null;
    const storedPercentage = storedFlowValue ? ((storedFlowValue / planMaxFps) * 100).toFixed(2) : null;

    const hasNoDevices = deviceCount === 0;

    const goToPublicCloudsLink = hideGoToPublicCloudsButton ? null : (
      <Link to="/v4/settings/clouds">
        <Flex alignItems="center">
          <Text mr="4px" textDecoration="underline">
            Go to Public Clouds
          </Text>
          <FiExternalLink />
        </Flex>
      </Link>
    );

    const planDescription = description ? (
      <Text color="muted" as="div" mb={1}>
        {description}
      </Text>
    ) : null;

    if (loading) {
      return (
        <Box>
          <LoadingSkeleton height={250} width="100%" />
        </Box>
      );
    }

    if (error) {
      return (
        <Box>
          {planDescription}
          <PlanCardVizHeader
            leftText="Utilization:"
            lookback={{ value: selectedTimePeriod || '1_day', label: '' }}
            handleTimePeriodChange={this.handleTimePeriodChange}
            showSelect
            timePeriodOptions={this.timePeriodOptions}
            externalLinkElement={goToPublicCloudsLink}
          />
          <Text as="div" muted my={3} textAlign="center">
            Error loading Plan FPS data
          </Text>
        </Box>
      );
    }

    if (hasNoDevices) {
      return (
        <Box>
          {planDescription}
          <PlanCardVizHeader
            leftText="Utilization:"
            lookback={{ value: selectedTimePeriod || '1_day', label: '' }}
            handleTimePeriodChange={this.handleTimePeriodChange}
            showSelect
            timePeriodOptions={this.timePeriodOptions}
            externalLinkElement={goToPublicCloudsLink}
          />
          <Box p={2} backgroundColor="#f7f9fa" borderRadius="3px" mt={4}>
            <Flex flexDirection="column" alignItems="center">
              <Text>No Cloud Export Devices are configured to collect flow.</Text>
              <Link to="/v4/setup/clouds" blank rel="noopener noreferrer">
                Configure a cloud export to collect flow logs
              </Link>
              <Text>and we will display utilization metrics here.</Text>
              <Box mt={3}>
                <Link to="https://kb.kentik.com/docs/cloud-overview" blank rel="noopener noreferrer">
                  Learn more about Public Clouds and Flow Logs
                </Link>
              </Box>
            </Flex>
          </Box>
        </Box>
      );
    }

    const DataResolutionTooltipContent = (
      <>
        <Text as="div">Kentik provides intelligent downsampling</Text>
        <Text as="div">by dynamically adjusting data resolution</Text>
        <Text as="div">to ensure you stay below your max FPS.</Text>
        <Link to="https://kb.kentik.com/docs/flow-overview#flow-sampling" blank>
          Knowledge Base
        </Link>
      </>
    );

    return (
      <Box>
        {planDescription}
        {/* Utilization Section Header */}
        <PlanCardVizHeader
          leftText="Utilization:"
          lookback={{ value: selectedTimePeriod || '1_day', label: '' }}
          handleTimePeriodChange={this.handleTimePeriodChange}
          showSelect
          timePeriodOptions={this.timePeriodOptions}
          externalLinkElement={goToPublicCloudsLink}
        />

        {/* Chart */}
        <Box mb={3}>
          <Highcharts options={this.chartConfig} styledMode={false} />
        </Box>

        {/* Stats Table */}
        <Box mt={3} p={2} backgroundColor="#f7f9fa" borderRadius="3px">
          {$auth?.isSudoer && (
            <>
              <SudoTaggedText
                tagPosition={SudoTagPosition.Left}
                tagProps={{ small: true }}
                justifyContent="space-between"
                mb={1}
              >
                <Flex flex={1} justifyContent="space-between" alignItems="center">
                  <Text fontWeight="bold">Raw FPS:</Text>
                  <Text>{rawFlowValue ? addCommas(Math.round(rawFlowValue)) : 'N/A'} flow/s</Text>
                </Flex>
              </SudoTaggedText>

              <SudoTaggedText
                tagPosition={SudoTagPosition.Left}
                tagProps={{ small: true }}
                justifyContent="space-between"
                mb={1}
              >
                <Flex flex={1} justifyContent="space-between" alignItems="center">
                  <Text fontWeight="bold">Avg Configured Sampling Rate:</Text>
                  <Flex alignItems="center">
                    <Text>{avgConfiguredSamplingRate || 'N/A'}</Text>
                    <DataResolutionIndicator
                      dataResolution={dataResolution}
                      avgConfiguredSamplingRate={avgConfiguredSamplingRate}
                      useConfiguredSamplingRate
                    />
                  </Flex>
                </Flex>
              </SudoTaggedText>
            </>
          )}

          <Flex justifyContent="space-between" alignItems="center" mb={1}>
            <Text fontWeight="bold">Received FPS (P95):</Text>
            <Text>
              {receivedFlowValue ? `${addCommas(Math.round(receivedFlowValue))} flow/s` : 'N/A'}
              {receivedPercentage && (
                <Text color="muted" ml={1}>
                  {receivedPercentage}%
                </Text>
              )}
            </Text>
          </Flex>

          <Flex justifyContent="space-between" alignItems="center" mb={1}>
            <Text fontWeight="bold">Stored FPS (P95):</Text>
            <Text>
              {storedFlowValue ? `${addCommas(Math.round(storedFlowValue))} flow/s` : 'N/A'}
              {storedPercentage && (
                <Text color="muted" ml={1}>
                  {storedPercentage}%
                </Text>
              )}
            </Text>
          </Flex>

          {dataResolution !== 'ok' && (
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontWeight="bold">
                Data Resolution{' '}
                <Tooltip
                  content={DataResolutionTooltipContent}
                  interactionKind={PopoverInteractionKind.HOVER}
                  hoverCloseDelay={100}
                >
                  <Icon icon="info-sign" ml="4px" size={12} />
                </Tooltip>
              </Text>
              <Flex alignItems="center">
                <DataResolutionIndicator dataResolution={dataResolution} useConfiguredSamplingRate={false} />
              </Flex>
            </Flex>
          )}
        </Box>
      </Box>
    );
  }
}
